/**
 * The signed-in account's own page.
 *
 * Everything here is private by construction — it comes from `/me/dashboard`,
 * which never answers for anyone but the session that asked. The public
 * counterpart is `Profile.tsx`, and the two deliberately do not share a
 * component: the difference between them is *what may be shown*, and a shared
 * component with a `private` prop is how that difference eventually leaks.
 */
import { Link } from 'react-router-dom'

import { Avatar } from '../components/Avatar'
import { AssetLogo } from '../components/AssetLogo'
import { EmptyState, RarityChip, SectionHeading, Skeleton, Stat, rarityClass } from '../components/ui'
import { useDashboard } from '../hooks/queries'
import { useAuth } from '../hooks/useAuth'
import type { DashboardResponse, ShowcasePull } from '../lib/api'
import { cents, dateOnly, multiple, percent, timeAgo } from '../lib/format'

export function DashboardPage() {
  const { user, connectAndSignIn } = useAuth()
  const { data, isLoading } = useDashboard(Boolean(user))

  if (!user) {
    return (
      <div className="py-10">
        <EmptyState
          title="Sign in to see your dashboard"
          body="Your account lives behind your wallet signature, so there is nothing to show until you sign in."
          action={
            <button className="btn btn-primary btn-sm" onClick={() => void connectAndSignIn()}>
              Connect wallet
            </button>
          }
        />
      </div>
    )
  }

  if (isLoading || !data) {
    return (
      <div className="space-y-4 py-6">
        <Skeleton className="h-40" />
        <Skeleton className="h-32" />
        <Skeleton className="h-72" />
      </div>
    )
  }

  return (
    <div className="py-6">
      <Header data={data} />

      <section className="mt-6">
        <Money data={data} />
      </section>

      <section className="mt-10">
        <SectionHeading
          title="Best pulls"
          sub="Your highest-value cards, whether you kept them or sold them."
        />
        {data.bestPulls.length === 0 ? (
          <EmptyState
            title="Nothing pulled yet"
            body="Open a pack and your best cards show up here."
            action={
              <Link to="/packs" className="btn btn-primary btn-sm">
                Browse packs
              </Link>
            }
          />
        ) : (
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-6">
            {data.bestPulls.map((pull) => (
              <PullTile key={pull.id} pull={pull} />
            ))}
          </div>
        )}
      </section>

      <section className="mt-10">
        <SectionHeading
          title="Recent rips"
          sub="Your last eight opens, newest first."
          action={
            <Link to="/fairness" className="btn btn-ghost btn-sm">
              Verify a rip
            </Link>
          }
        />
        <RecentOpens opens={data.recentOpens} />
      </section>
    </div>
  )
}

/* ------------------------------------------------------------------ *
 * Header
 * ------------------------------------------------------------------ */

function Header({ data }: { data: DashboardResponse }) {
  const { user, stats } = data
  const identity = {
    username: user.username,
    handle: user.username ?? 'you',
    avatarKind: user.avatarKind,
    avatarRef: user.avatarRef,
    seed: user.avatarSeed,
  }

  return (
    <div className="panel relative overflow-hidden">
      <div
        className="absolute inset-0"
        style={{
          background:
            'radial-gradient(70% 120% at 15% 0%, var(--color-brand-wash) 0%, transparent 60%)',
        }}
        aria-hidden
      />
      <div className="relative flex flex-wrap items-start gap-5 p-6 sm:p-8">
        <Avatar identity={identity} size={88} />

        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-3">
            <h1 className="text-2xl font-bold tracking-tight sm:text-3xl">
              {user.username ?? 'Your account'}
            </h1>
            {!user.profilePublic && (
              <span
                className="px-2 py-0.5 text-[0.625rem] font-bold tracking-wider uppercase"
                style={{ background: 'var(--color-surface-3)', color: 'var(--color-faint)' }}
              >
                Private
              </span>
            )}
          </div>

          {user.bio ? (
            <p className="mt-2 max-w-lg text-sm leading-relaxed" style={{ color: 'var(--color-muted)' }}>
              {user.bio}
            </p>
          ) : (
            <p className="mt-2 text-sm" style={{ color: 'var(--color-faint)' }}>
              No bio yet.
            </p>
          )}

          <p className="mt-2 text-[0.6875rem]" style={{ color: 'var(--color-faint)' }}>
            Joined {dateOnly(user.createdAt)}
            {stats.firstOpenAt ? ` · first rip ${dateOnly(stats.firstOpenAt)}` : ''}
          </p>
        </div>

        <div className="flex shrink-0 gap-2">
          <Link to="/settings" className="btn btn-ghost btn-sm">
            Edit profile
          </Link>
          {user.profilePublic && user.username && (
            <Link to={`/u/${user.username}`} className="btn btn-quiet btn-sm">
              View public
            </Link>
          )}
        </div>
      </div>

      <div className="relative grid gap-6 border-t px-6 py-5 sm:grid-cols-4 sm:px-8">
        <Stat label="Packs opened" value={stats.opens.toLocaleString()} />
        <Stat label="Cards pulled" value={stats.cards.toLocaleString()} />
        <Stat
          label="Best rip"
          value={stats.bestMultiple === null ? '-' : multiple(stats.bestMultiple)}
          sub={stats.bestRarity ? `best card: ${stats.bestRarity}` : undefined}
          tone={stats.bestMultiple !== null && stats.bestMultiple >= 1 ? 'up' : undefined}
        />
        <Stat label="Favourite tier" value={stats.favouriteTier ?? '-'} />
      </div>
    </div>
  )
}

/* ------------------------------------------------------------------ *
 * Money
 * ------------------------------------------------------------------ */

/**
 * Balance, vault and lifetime return in one row.
 *
 * Realised RTP is shown next to the tier's published one wherever it appears on
 * this site, and this is no exception: on a negative-expectation product the
 * number a player most needs is what *they* actually got back, not what the
 * table promised.
 */
function Money({ data }: { data: DashboardResponse }) {
  const { user, stats, vault } = data
  const vaultChange = vault.valueCents - vault.basisCents
  const net = stats.returnedCents - stats.stakedCents

  return (
    <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
      <div className="panel p-5">
        <div className="eyebrow">Balance</div>
        <div className="tnum mt-1 text-2xl font-bold">{cents(user.balanceCents)}</div>
        <Link to="/packs" className="btn btn-primary btn-sm mt-4 w-full">
          Open a pack
        </Link>
      </div>

      <div className="panel p-5">
        <div className="eyebrow">Vault</div>
        <div className="tnum mt-1 text-2xl font-bold">{cents(vault.valueCents)}</div>
        <div className="mt-1 text-xs" style={{ color: 'var(--color-faint)' }}>
          {vault.count} card{vault.count === 1 ? '' : 's'} ·{' '}
          <span style={{ color: vaultChange >= 0 ? 'var(--color-up)' : 'var(--color-down)' }}>
            {vaultChange >= 0 ? '+' : '−'}
            {cents(Math.abs(vaultChange))}
          </span>{' '}
          since pulled
        </div>
        <Link to="/vault" className="btn btn-ghost btn-sm mt-4 w-full">
          Open vault
        </Link>
      </div>

      <div className="panel p-5">
        <div className="eyebrow">Staked / returned</div>
        <div className="tnum mt-1 text-2xl font-bold">{cents(stats.stakedCents)}</div>
        <div className="tnum mt-1 text-xs" style={{ color: 'var(--color-faint)' }}>
          {cents(stats.returnedCents)} back ·{' '}
          <span style={{ color: net >= 0 ? 'var(--color-up)' : 'var(--color-down)' }}>
            {net >= 0 ? '+' : '−'}
            {cents(Math.abs(net))}
          </span>
        </div>
      </div>

      <div className="panel p-5">
        <div className="eyebrow">Your realised RTP</div>
        <div className="tnum mt-1 text-2xl font-bold">
          {stats.realisedRtp === null ? '-' : percent(stats.realisedRtp, 1)}
        </div>
        <p className="mt-2 text-[0.6875rem] leading-relaxed" style={{ color: 'var(--color-faint)' }}>
          {stats.opens < 20
            ? `Only ${stats.opens} open${stats.opens === 1 ? '' : 's'} so far, far too few for this to mean anything yet.`
            : 'What you actually got back, against a published 54–63%.'}
        </p>
      </div>
    </div>
  )
}

/* ------------------------------------------------------------------ *
 * Showcase
 * ------------------------------------------------------------------ */

export function PullTile({ pull }: { pull: ShowcasePull }) {
  return (
    <article className={`panel ${rarityClass(pull.rarity)} p-4`}>
      <div className="flex items-start gap-3">
        <AssetLogo kind={pull.kind} symbol={pull.symbol} rarity={pull.rarity} size={40} />
        <div className="min-w-0 flex-1">
          <div className="flex items-baseline justify-between gap-2">
            <span className="truncate text-sm font-bold">{pull.symbol}</span>
            <span className="tnum shrink-0 text-sm font-bold">{cents(pull.valueCents)}</span>
          </div>
          <div className="truncate text-[0.6875rem]" style={{ color: 'var(--color-faint)' }}>
            {pull.name}
          </div>
        </div>
      </div>

      <div className="mt-3 flex items-center justify-between gap-2">
        <RarityChip rarity={pull.rarity} />
        <span className="tnum text-xs font-semibold" style={{ color: 'var(--color-up)' }}>
          {multiple(pull.multiple)}
        </span>
      </div>

      <div className="mt-2 truncate text-[0.6875rem]" style={{ color: 'var(--color-faint)' }}>
        {pull.packName} · {timeAgo(pull.openedAt)}
      </div>
    </article>
  )
}

function RecentOpens({ opens }: { opens: DashboardResponse['recentOpens'] }) {
  if (opens.length === 0) {
    return <EmptyState title="No rips yet" body="Your opens will be listed here with their results." />
  }

  return (
    <div className="panel divide-y overflow-hidden">
      {opens.map((open) => {
        const up = open.totalCents >= open.priceCents
        return (
          <div key={open.id} className="flex items-center gap-4 px-4 py-3">
            <div className="min-w-0 flex-1">
              <div className="truncate text-sm font-semibold">{open.packName}</div>
              <div className="truncate text-[0.6875rem]" style={{ color: 'var(--color-faint)' }}>
                {cents(open.priceCents)} · {timeAgo(open.openedAt)} · best {open.best}
              </div>
            </div>
            <div className="text-right">
              <div
                className="tnum text-sm font-bold"
                style={{ color: up ? 'var(--color-up)' : 'var(--color-down)' }}
              >
                {multiple(open.multiple)}
              </div>
              <div className="tnum text-[0.6875rem]" style={{ color: 'var(--color-faint)' }}>
                {cents(open.totalCents)}
              </div>
            </div>
          </div>
        )
      })}
    </div>
  )
}
